import { groupBy, min, nonNullable, partitionBy, sortBy, without } from 'lib/array/arrayUtils'
import { buildRoomPackageOptionsViewOrderLPC } from 'lib/offer/offerPageUtil'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'

interface RateView {
  price: number;
  boardCode: App.RateBoardCode;
  type: 'LPC' | 'bedbank';
  lpcRate: OfferPackageOptionView | undefined;
  refundable: boolean;
  bedbankRate: {
    rate: App.BedbankRate;
    view: App.BedbankOfferPackageView;
  } | undefined,

}

export const getCombinedRates = (
  packageViews: Array<OfferPackageOptionView>,
  bedbankViews: Array<App.BedbankOfferPackageView>,
  availableOptions: Array<OfferPackageOptionView>,
  previewMode: boolean,
  utm_content: string | null,
  roomRateId?: string,
  setIsRoomRateUnmatched?: (value: React.SetStateAction<boolean>) => void,
  isHighlighted?: boolean,
): Array<RateView> => {
  const [roomFitsCapacity] = [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable) || bedbankViews.some(view => view.available),
  ]

  const rateOptionList = () => {
    if (isHighlighted && roomRateId && availableOptions.length >= 1) {
      /**
        * Modifying the rateOption array so we have the highlightedRateOption on top
       **/
      const option = availableOptions.find((view) => view.package.roomRate?.id === roomRateId)
      if (option) {
        return [option, ...without(availableOptions, option)]
      } else if (utm_content == 'gha-lpc') {
        // if roomRateId is associated with user currency (eg: cb4ebd70-33d1-11ed-9609-ffb70b8fec99_USD ) remove the currency and extract the rate id for comparison
        const baseRateId = roomRateId?.split('_')[0]
        const option = availableOptions.find((view) => view.package.roomRate?.id === baseRateId)
        if (option) {
          return [option, ...without(availableOptions, option)]
        } else {
          setIsRoomRateUnmatched?.(true)
        }
      }
    }
    return availableOptions
  }

  const bedbankViewRateRecords = () => {
    if (!roomFitsCapacity) {
      return []
    }
    return bedbankViews.flatMap(view => view.rates.map(rate => ({
      rate,
      view,
    })))
  }

  // now we combine our LPC and bedbank rates
  const allRates: Array<RateView> = rateOptionList().map((rate): RateView => ({
    price: rate.memberPricing?.price || rate.pricing?.price,
    boardCode: rate.package.boardCode ?? 'LE',
    refundable: rate.refundable,
    type: 'LPC',
    lpcRate: rate,
    bedbankRate: undefined,
  }))

  if (previewMode) {
    // preview mode on property pages doesn't include bedbank rates and doesn't do any
    // any extra de-duping. Preview users want to see *all* (LPC) rates
    // those without a price of 0 will be the placed at the end
    return sortBy(allRates, (rate) => rate.price || Number.MAX_SAFE_INTEGER, 'asc')
  }

  allRates.push(...bedbankViewRateRecords().map((rate): RateView => ({
    price: (rate.rate.totals.memberTotal || rate.rate.totals.total) ?? 0,
    boardCode: rate.rate.boardCode,
    refundable: rate.rate.refundable,
    type: 'bedbank',
    lpcRate: undefined,
    bedbankRate: rate,
  })))

  // now dedupe them based on board code
  const ratesByCode = groupBy(allRates, rate => rate.boardCode ?? 'LE')
  const deDupedRates = Array.from(ratesByCode.entries()).flatMap(([boardCode, rateGroup]) => {
    if (boardCode === 'LE') {
      // this group is already unique, just pass them on
      return rateGroup
    }

    // now group again by cancellation policy
    const [refundableRates, nonRefundableRates] = partitionBy(rateGroup, group => group.refundable)
    // board codes describe common sets of inclusions, same board code = basically the same rate
    // so just return the cheapest one. In the event we have LPC and bedbank the same price
    // use the LPC one instead until we have margin info
    return nonNullable([
        min(sortBy(refundableRates, rate => rate.type === 'LPC' ? 1 : 0, 'desc'), rate => rate.price)!,
        min(sortBy(nonRefundableRates, rate => rate.type === 'LPC' ? 1 : 0, 'desc'), rate => rate.price)!,
    ])
  })

  return sortBy(deDupedRates, (rate) => rate.price, 'asc')
}

export const getPackageOptionsByRoom = (packageOptions: Array<OfferPackageOptionView>, LPCAdvancedSortingEnabled: boolean, bedbankPackageViews: Array<App.BedbankOfferPackageView>, mapping?: App.PropertyOfferMapping) => {
  const lpcPackages = buildRoomPackageOptionsViewOrderLPC(packageOptions, LPCAdvancedSortingEnabled)
  // now we gently insert the bedbank options
  const bedbankPackagesByRoomId = groupBy(bedbankPackageViews, view => view.package.roomId)
  // now created a combined view of LPC and bedbank

  const views = lpcPackages.map(pkg => {
    const mappedBedbankRoomIds = mapping?.rooms[pkg.roomId]?.bedbankRoomIds ?? []
    const bedbankViews = nonNullable(mappedBedbankRoomIds.flatMap(id => bedbankPackagesByRoomId.get(id)))
    return {
      roomId: pkg.roomId,
      lpc: pkg.packageViews,
      bedbank: bedbankViews,
    }
  })

  // Property page just always sorts rooms by best price
  return sortBy(views, view => {
    const allPrices = [
      ...view.lpc.filter(lpc => lpc.capacityValid && lpc.hotelAvailable).map(lpc => lpc.memberPricing?.price || lpc.pricing.price || Number.MAX_SAFE_INTEGER),
      ...view.bedbank.filter(bedbank => bedbank.capacityValid && bedbank.available).map(bedbank => bedbank.cheapestRate?.totals.memberInclusive || bedbank.cheapestRate?.totals.inclusive || Number.MAX_SAFE_INTEGER),
      Number.MAX_SAFE_INTEGER,
    ]

    return min(allPrices)
  }, 'asc')
}
